import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ChevronLeft, ChevronRight, Loader2, Minus, Plus, Bookmark, BookmarkCheck } from "lucide-react";

// Self-hosted worker (copied to /public) so the reader works offline and on the live domain.
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

// In-app canvas PDF reader (mobile + desktop, read-only). Reports the current page
// up via onPageChange (used for resume + notes), can be told to jump to a page via
// `jumpTo`, and lets signed-in readers bookmark the current page (onToggleBookmark).
export default function PdfReader({
  file,
  labels = {},
  initialPage = 0,
  jumpTo = 0,
  onPageChange = null,
  bookmarkedPages = [],
  onToggleBookmark = null,
}) {
  const wrapRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [width, setWidth] = useState(0);
  const [scale, setScale] = useState(1);
  const [error, setError] = useState(false);
  const lastReported = useRef(0);

  useEffect(() => {
    const measure = () => { if (wrapRef.current) setWidth(Math.min(wrapRef.current.clientWidth - 24, 820)); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const onLoad = ({ numPages: n }) => {
    setNumPages(n);
    const start = initialPage && initialPage > 1 && initialPage <= n ? initialPage : 1;
    setPage(start);
  };

  // Jump to a requested page (bookmark click). `jumpTo` = {page, n} so repeat
  // clicks on the same bookmark still re-trigger via the changing nonce.
  useEffect(() => {
    const jp = jumpTo && jumpTo.page;
    if (jp && jp > 0 && numPages && jp <= numPages) {
      setPage(jp);
      if (wrapRef.current) wrapRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [jumpTo, numPages]);

  // Report page changes upward (resume progress + "add note for page N").
  useEffect(() => {
    if (page && page !== lastReported.current) { lastReported.current = page; onPageChange && onPageChange(page); }
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (d) => setPage((p) => Math.min(Math.max(1, p + d), numPages || 1));
  const isMarked = bookmarkedPages.includes(page);

  if (error) return <p className="py-10 text-center text-ink-3">{labels.error || "Unable to load this document."}</p>;

  return (
    <div ref={wrapRef} data-testid="ebook-viewer" className="flex flex-col items-center">
      <Document
        file={file}
        onLoadSuccess={onLoad}
        onLoadError={() => setError(true)}
        loading={<div className="flex items-center gap-2 py-16 text-ink-3"><Loader2 className="h-5 w-5 animate-spin" /> {labels.loading || "Loading…"}</div>}
        className="w-full"
      >
        {width > 0 && (
          <div className="flex justify-center overflow-auto">
            <Page
              pageNumber={page}
              width={width * scale}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              loading={<div className="flex items-center gap-2 py-16 text-ink-3"><Loader2 className="h-5 w-5 animate-spin" /></div>}
              className="shadow-lg"
            />
          </div>
        )}
      </Document>

      {numPages > 0 && (
        <div className="sticky bottom-3 z-10 mt-4 flex items-center gap-1 rounded-full border border-line bg-surface/95 px-2 py-1.5 shadow-lg backdrop-blur">
          <button onClick={() => go(-1)} disabled={page <= 1} data-testid="pdf-prev" className="flex h-9 w-9 items-center justify-center rounded-full text-ink-2 transition-colors hover:bg-parchment disabled:opacity-40"><ChevronLeft className="h-5 w-5" /></button>
          <span className="px-2 text-sm tabular-nums text-ink-2" data-testid="pdf-page-indicator">{page} / {numPages}</span>
          <button onClick={() => go(1)} disabled={page >= numPages} data-testid="pdf-next" className="flex h-9 w-9 items-center justify-center rounded-full text-ink-2 transition-colors hover:bg-parchment disabled:opacity-40"><ChevronRight className="h-5 w-5" /></button>
          <span className="mx-1 h-5 w-px bg-line" />
          <button onClick={() => setScale((s) => Math.max(0.6, +(s - 0.2).toFixed(1)))} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-2 transition-colors hover:bg-parchment"><Minus className="h-4 w-4" /></button>
          <button onClick={() => setScale((s) => Math.min(2.4, +(s + 0.2).toFixed(1)))} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-2 transition-colors hover:bg-parchment"><Plus className="h-4 w-4" /></button>
          {onToggleBookmark && (
            <>
              <span className="mx-1 h-5 w-px bg-line" />
              <button onClick={() => onToggleBookmark(page)} title={labels.bookmark || "Bookmark this page"} data-testid="pdf-bookmark" className={`flex h-9 items-center gap-1.5 rounded-full px-3 text-sm transition-colors hover:bg-parchment ${isMarked ? "text-saffron" : "text-ink-2"}`}>
                {isMarked ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
                <span className="hidden sm:inline">{isMarked ? (labels.bookmarked || "Bookmarked") : (labels.bookmark || "Bookmark")}</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
